import { Component, OnDestroy, OnInit } from "@angular/core";
import { FormBuilder, FormGroup } from "@angular/forms";
import { CustomValidators } from "app/pages/A2-utils/custom-validators";
import { Subscription } from "rxjs/Subscription";
import { QuizClass, QuizQuestionClass } from "../quiz.class";
import { QuizQuestionFormValue } from "../quiz-form-values.interface";
import { QuizService } from "../quiz.service";
import { T2LoadingService } from "app/trip2-theme/components/t2-loading/t2-loading.service";
import { SweetAlertHelper } from "app/core/helpers/sweet-alert-helper.service";
import { finalize } from "rxjs/operators";

@Component({
  selector: "app-quiz-question-form",
  templateUrl: "./quiz-question-form.component.html",
})
export class QuizQuestionFormComponent implements OnInit, OnDestroy {
  quizClass: QuizClass;
  quizQuestionForm: FormGroup;
  private selectedQuestionIndex: number = null;
  private subscriptions: Subscription[] = [];

  constructor(
    private fb: FormBuilder,
    private _serviceQuiz: QuizService,
    private sweetAlertHelper: SweetAlertHelper,
    private loading: T2LoadingService
  ) {
    this.initForm();
  }

  ngOnInit(): void {
    this.initSubscriptions();
  }

  get isQuestionFormValid(): boolean {
    return this.quizQuestionForm.valid;
  }

  private initForm(): void {
    this.quizQuestionForm = this.fb.group({
      quizId: [null],
      questionTypeId: [1],
      quizQuestionId: [null],
      description: [null, [CustomValidators.required]],
    });
  }

  private initSubscriptions(): void {
    this.subscriptions.push(
      this._serviceQuiz.quizData$.subscribe({
        next: (data) => {
          this.quizClass = data;
        },
        error: (err) => {
          this.sweetAlertHelper.captureException(err);
        },
      })
    );
    this.subscriptions.push(
      this._serviceQuiz.questionFormAction$.subscribe({
        next: (action) => {
          if (!action) {
            return;
          }
          if (action.mode === "new") {
            this.openNew(action.quizId);
          } else if (action.mode === "edit" && action.data && action.index != null) {
            this.openEdit(action.data, action.index);
          }
        },
        error: (err) => {
          this.sweetAlertHelper.captureException(err);
        },
      })
    );
  }

  openNew(quizId: number) {
    if (!this.quizClass) {
      return;
    }
    this.selectedQuestionIndex = null;
    this.quizQuestionForm.patchValue({
      quizId: quizId,
      questionTypeId: 1,
      quizQuestionId: null,
      description: null,
    });
  }

  openEdit(data: QuizQuestionClass, index: number) {
    if (!this.quizClass) {
      return;
    }
    this.selectedQuestionIndex = index;
    this.quizQuestionForm.patchValue({
      quizId: data.quizid,
      questionTypeId: data.questiontypeid,
      quizQuestionId: data.quizquestionid,
      description: data.description,
    });
  }

  private mapQuizQuestionFormToApi(form: QuizQuestionFormValue): QuizQuestionClass {
    const m = new QuizQuestionClass();
    m.quizid = form.quizId;
    m.questiontypeid = form.questionTypeId;
    m.quizquestionid = form.quizQuestionId;
    m.description = form.description;
    return m as QuizQuestionClass;
  }

  submitFormQuestion() {
    if (!this.quizClass) {
      return;
    }
    if (!this.quizQuestionForm.valid) {
      return;
    }
    const form = this.mapQuizQuestionFormToApi(this.quizQuestionForm.value as QuizQuestionFormValue);
    if (this.selectedQuestionIndex == null) {
      this.newFormQuestion(form);
    } else {
      this.updateFormQuestion(form);
    }
  }

  private newFormQuestion(form: QuizQuestionClass) {
    this.loading.show();
    this.subscriptions.push(
      this._serviceQuiz
        .postQuizQuestion(form)
        .pipe(finalize(() => this.loading.hiden()))
        .subscribe({
          next: (res: QuizQuestionClass) => {
            if (res) {
              this._serviceQuiz.addQuestionToQuiz({
                quizid: form.quizid,
                quizquestionid: res.quizquestionid,
                description: form.description,
                quizanswer: [],
                questiontypeid: 1,
              });
              this.resetAfterSuccess();
            }
          },
          error: (err) => {
            this.sweetAlertHelper.captureException(err);
          },
        })
    );
  }

  private updateFormQuestion(form: QuizQuestionClass) {
    this.loading.show();
    this.subscriptions.push(
      this._serviceQuiz
        .putQuizQuestion(form)
        .pipe(finalize(() => this.loading.hiden()))
        .subscribe({
          next: (res: QuizQuestionClass) => {
            if (res) {
              this._serviceQuiz.updateQuestionDescription(
                this.selectedQuestionIndex,
                form.description
              );
              this.resetAfterSuccess();
            }
          },
          error: (err) => {
            this.sweetAlertHelper.captureException(err);
          },
        })
    );
  }

  private resetAfterSuccess() {
    this.quizQuestionForm.reset();
    this.selectedQuestionIndex = null;
    this._serviceQuiz.setQuestionFormSaved(true);
  }

  ngOnDestroy(): void {
    if (this.subscriptions && this.subscriptions.length) {
      this.subscriptions.forEach(subscription => {
        if (subscription) {
          subscription.unsubscribe();
        }
      });
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/pages/quiz/quiz-question-form/quiz-question-form.component.ts